import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useFormikContext } from "formik";
import {
  GenericTextField,
  GenericDateField,
  GenericLookupField,
} from "../../../components/common/FormFields";
import {
  DriverChargedStatusLabels,
  DotInspectionCompanyDto,
  DotInspectionLookupDto,
} from "../../../types/dotInspection";
import { DotInspectionFormValues } from "../validation/dotInspectionFormValidation";
import DotInspectionPendingFilesSection, {
  PendingFile,
} from "./DotInspectionPendingFilesSection";

interface DotInspectionFormLayoutProps {
  availableCompanies?: DotInspectionCompanyDto[];
  availableDrivers?: DotInspectionLookupDto[];
  availableTrucks?: DotInspectionLookupDto[];
  availableTrailers?: DotInspectionLookupDto[];
  pendingFiles?: PendingFile[];
  onPendingFilesChange?: (files: PendingFile[]) => void;
}

const driverChargedOptions = Object.entries(DriverChargedStatusLabels).map(
  ([value, label]) => ({
    id: Number(value),
    label,
  })
);

const DotInspectionFormLayout: React.FC<DotInspectionFormLayoutProps> = ({
  availableCompanies = [],
  availableDrivers = [],
  availableTrucks = [],
  availableTrailers = [],
  pendingFiles = [],
  onPendingFilesChange,
}) => {
  const formik = useFormikContext<DotInspectionFormValues>();

  const companyLookupOptions = availableCompanies.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  return (
    <Box>
      <Card sx={{ mb: 3, mt: 1 }}>
        <CardContent sx={{ pt: 3 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            Inspection Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <GenericDateField
                name="inspectionDate"
                label="Date"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericLookupField
                name="claimCompany"
                label="Company"
                options={companyLookupOptions}
                idFieldName="claimCompanyId"
                nameFieldName="claimCompanyName"
                placeholder="Select or type company name"
                allowCreate={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableDrivers}
                getOptionLabel={(option) => option.name}
                value={
                  availableDrivers.find(
                    (d) => d.id === formik.values.driverEmployeeId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "driverEmployeeId",
                    newValue?.id ?? null
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver"
                    placeholder="Search drivers..."
                    error={
                      formik.touched.driverEmployeeId &&
                      Boolean(formik.errors.driverEmployeeId)
                    }
                    helperText={
                      formik.touched.driverEmployeeId &&
                      typeof formik.errors.driverEmployeeId === "string"
                        ? formik.errors.driverEmployeeId
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableTrucks}
                getOptionLabel={(option) => option.name}
                value={
                  availableTrucks.find(
                    (t) => t.id === formik.values.vehicleId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("vehicleId", newValue?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Truck"
                    required
                    placeholder="Search trucks..."
                    error={
                      formik.touched.vehicleId &&
                      Boolean(formik.errors.vehicleId)
                    }
                    helperText={
                      formik.touched.vehicleId &&
                      typeof formik.errors.vehicleId === "string"
                        ? formik.errors.vehicleId
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableTrailers}
                getOptionLabel={(option) => option.name}
                value={
                  availableTrailers.find(
                    (t) => t.id === formik.values.trailerId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("trailerId", newValue?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trailer"
                    placeholder="Search trailers..."
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Points"
                name="points"
                type="number"
                value={formik.values.points}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.points && Boolean(formik.errors.points)
                }
                helperText={
                  formik.touched.points &&
                  typeof formik.errors.points === "string"
                    ? formik.errors.points
                    : ""
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                name="amount"
                type="number"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.amount && Boolean(formik.errors.amount)
                }
                helperText={
                  formik.touched.amount &&
                  typeof formik.errors.amount === "string"
                    ? formik.errors.amount
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: "0.01" }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={driverChargedOptions}
                getOptionLabel={(option) => option.label}
                value={
                  formik.values.driverCharged != null
                    ? driverChargedOptions.find(
                        (d) => d.id === formik.values.driverCharged
                      ) || null
                    : null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "driverCharged",
                    newValue?.id ?? null
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver Charged"
                    placeholder="Search..."
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <GenericTextField
                name="description"
                label="Description"
                multiline
                rows={4}
                placeholder="Enter inspection description..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <DotInspectionPendingFilesSection
            pendingFiles={pendingFiles}
            onChange={onPendingFilesChange ?? (() => {})}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DotInspectionFormLayout;
